import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  UserCircle, 
  CreditCard, 
  Users2, 
  Package, 
  ChevronDown, 
  ChevronUp, 
  Plus,
  CheckSquare,
  FileText,
  Upload,
  Loader2,
  Check,
  Receipt,
  CheckCircle,
  Download,
  Eye
} from 'lucide-react';
import { ProjectManagerAssignment, ProjectManagerAssignmentForm } from './overview/ProjectManagerAssignment';
import { PaymentManagement } from './overview/PaymentManagement';
import { OverviewActivityTables, TeamAssignmentForm } from './overview/OverviewActivityTables';
import { ProjectInventoryTab } from './ProjectInventoryTab';
import { ProjectTasksTab } from './ProjectTasksTab';
import { useProjectMaterialRequests } from '../hooks/useProjectMaterialRequests';
// import { CreateTowerExpenseModal } from '../../tower-general-expenses/components/CreateTowerExpenseModal';
import { ProjectDocumentsTab } from './ProjectDocumentsTab';
import Toast from "@/components/ui/Toast";
import Modal from "@/components/ui/Modal";
import { DataTable } from '@/components/ui/DataTable';
import { useOverviewTables } from '../hooks/useOverviewTables';
import { TEAM_COLUMNS, TASK_COLUMNS, TRANSACTION_COLUMNS, EXPENSE_COLUMNS, DOCUMENT_COLUMNS } from '../constants/overviewTableConstants';
import { ScheduleTaskModal } from './modals/ScheduleTaskModal';
import { AddWorkUpdateModal } from './modals/AddWorkUpdateModal';
import { TradeWorkUpdateModal } from './modals/TradeWorkUpdateModal';
import { TradeWorkUpdatesHistoryModal } from './modals/TradeWorkUpdatesHistoryModal';
import { TaskDetailsModal } from './modals/TaskDetailsModal';
import { hasProjectInstallationTeam, hasMaterialReceiptForTaskAssignment, fieldOfficersEligibleForTasks } from '../utils/projectTeamAccess';
import { PROJECT_PIPELINE_STAGES } from '../constants/projectConstants';
import { getActivePipelineIndex } from '../constants/projectPipelineStages';
import api, { BASE_URL } from "@/lib/API";
import { projectService } from '../services/projectService';

const apiFileBase = () => String(BASE_URL).replace(/\/api\/?$/, "");

// Helper Components for Accordion
const AccordionSection = ({ title, icon, children, isOpen, onToggle, isCompleted, action, statusLabel, statusColor }) => {
   return (
      <div className={`overflow-hidden shadow-sm transition-all duration-300 ${isOpen ? 'border-b border-[#f97316]' : ''}`}>
         <div className={`flex items-center justify-between hover:bg-slate-50 transition-colors ${isOpen ? 'bg-slate-50/50 border-b border-slate-100' : ''}`}>
            <button
               onClick={onToggle}
               className="flex-1 p-3 flex items-center justify-between cursor-pointer"
            >
               <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm border ${
                     isCompleted ? 'bg-emerald-50 border-emerald-100 text-emerald-500' : 'bg-white border-slate-100 text-slate-500'
                  }`}>
                     {icon}
                  </div>
                  <div className="flex flex-col items-start text-left">
                     <span className={`text-[14px] font-semibold tracking-tight ${
                        isCompleted ? 'text-emerald-700' : 'text-slate-700'
                     }`}>{title}</span>
                  </div>
               </div>
            </button>
            <div className="flex items-center gap-4 pr-4">
               {action}
               <div onClick={onToggle} className="cursor-pointer">
                  {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
               </div>
            </div>
         </div>
         {isOpen && (
            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
               {children}
            </div>
         )}
      </div>
   );
};

export const ProjectOverviewTab = ({ project, onUpdate, setActiveTab, tasksInventoryLocked = false }) => {
   const [openSections, setOpenSections] = useState({
      pm: false,
      designer: false,
      team: false,
      inventory: false,
      designs: false,
      designerApproval: false,
      finalQuotation: false,
      finalQuotationApproval: false,
      orderPlacement: false,
      tasks: false,
      documents: false,
      advance: false
   });
   const [toast, setToast] = useState(null);
   const [activeModal, setActiveModal] = useState(null); // 'pm', 'payment', 'team', 'task', 'upload', 'taskDetail'
   const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
   const [expenseInitialData, setExpenseInitialData] = useState(null);
   const [selectedTask, setSelectedTask] = useState(null);
   const [docModalState, setDocModalState] = useState({ hasFiles: false, submitting: false, canUpload: false });
   const [documentToApprove, setDocumentToApprove] = useState(null);
   const docFormRef = useRef();
   const [workUpdateTask, setWorkUpdateTask] = useState(null);
   const [updateTrade, setUpdateTrade] = useState(null);
   const [historyTrade, setHistoryTrade] = useState(null);
   const { transactionRows, formatRowDate, formatRowDateTime, tasks, refreshTasks } = useOverviewTables(project);
   const { requests, allDelivered } = useProjectMaterialRequests(project?._id);
   const [isApproving, setIsApproving] = useState(false);
   const [txTab, setTxTab] = useState('credits'); // 'credits' | 'expenses'
   const [localProgress, setLocalProgress] = useState({
      carpenter: project?.installationMilestones?.carpenterProgress || 0,
      electrician: project?.installationMilestones?.electricianProgress || 0,
      painter: project?.installationMilestones?.painterProgress || 0,
      plumber: project?.installationMilestones?.plumberProgress || 0
   });

   useEffect(() => {
      setLocalProgress({
         carpenter: project?.installationMilestones?.carpenterProgress || 0,
         electrician: project?.installationMilestones?.electricianProgress || 0,
         painter: project?.installationMilestones?.painterProgress || 0,
         plumber: project?.installationMilestones?.plumberProgress || 0
      });
   }, [project?.installationMilestones]);

   const currentUser = useMemo(() => {
      try {
         const userStr = localStorage.getItem('blum_user') || localStorage.getItem('venus_user') || localStorage.getItem('user');
         return userStr ? JSON.parse(userStr) : {};
      } catch (err) {
         return {};
      }
   }, []);

   const myTask = useMemo(() => {
      const uid = currentUser._id || currentUser.id;
      const isFO = String(currentUser.role || '').toLowerCase().includes('field');
      
      if (!tasks || !Array.isArray(tasks) || tasks.length === 0) return null;

      let task = tasks.find(t => {
         const aid = t.assignedOfficer?._id || t.assignedOfficer?.id || (typeof t.assignedOfficer === 'string' ? t.assignedOfficer : null);
         return aid && uid && String(aid) === String(uid) && t.status !== 'COMPLETED';
      });

      if (!task && isFO) {
         task = tasks.find(t => t.status !== 'COMPLETED');
      }

      return task;
   }, [tasks, currentUser]);

   const toggleSection = (section) => {
      setOpenSections(prev => ({
         ...prev,
         [section]: !prev[section]
      }));
   };

   const formatCurrency = (amount) => {
      if (!amount) return '0';
      return Number(amount).toLocaleString('en-IN');
   };

   const getInitials = (name) => {
      if (!name) return 'U';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
   };

   const handleApproveStage = async () => {
      setIsApproving(true);
      try {
         const data = await api.post(`/verve-projects/${project._id}/documents/approve-stage`);
         if (data) {
            onUpdate(data);
            setToast({ message: 'Documents stage approved successfully', type: 'success' });
         }
      } catch (err) {
         setToast({ message: err.response?.data?.message || err.message || 'Failed to approve documents stage', type: 'error' });
      } finally {
         setIsApproving(false);
      }
   };

   const isPmAssigned = !!project?.projectManager && (!Array.isArray(project.projectManager) || project.projectManager.length > 0);
   const isAdvancePaid = (project?.paymentSummary?.totalPaid || 0) > 0 || project?.advancePaymentStatus === 'Approved';
   const isTeamAssigned = (project?.fieldOfficers?.length || 0) > 0;
   const isTasksAssigned = 
      (isTeamAssigned && 
      project.fieldOfficers.every(officer => {
         const officerId = typeof officer === 'object' ? (officer._id || officer.id) : officer;
         return (tasks || []).some(t => {
            const taskOfficerId = typeof t.assignedOfficer === 'object' ? (t.assignedOfficer?._id || t.assignedOfficer?.id) : t.assignedOfficer;
            return String(taskOfficerId) === String(officerId);
         });
      }) &&
      (tasks || []).length > 0 &&
      tasks.every(t => t.status === 'COMPLETED')) ||
      ['balance update', 'documents', 'completed'].includes(String(project?.status || '').toLowerCase());

    const isDocumentsApproved = !!project?.documentsStageComplete;
    const activePipelineIndex = useMemo(() => {
       return getActivePipelineIndex(project?.projectStatus || project?.status, project?.installationMilestones || {}, {
          documentsStageComplete: !!project?.documentsStageComplete,
          teamAssigned: (project?.fieldOfficers?.length || 0) > 0,
          pmAssigned: !!project?.projectManager && (!Array.isArray(project.projectManager) || project.projectManager.length > 0)
       });
    }, [project]);
    const isInDocumentStage = PROJECT_PIPELINE_STAGES[activePipelineIndex] === 'Documents';

    const hasDocumentsUploaded = (project?.mergedDocuments?.length || 0) > 0 || (project?.documentUploads?.length || 0) > 0 || (project?.documents?.length || 0) > 0;
   const eligibleFieldOfficers = useMemo(() => fieldOfficersEligibleForTasks(project, requests), [project, requests]);
   const canAssignTask = hasProjectInstallationTeam(project) && eligibleFieldOfficers.length > 0 && !tasksInventoryLocked;
   const canUploadDocs = ['owner', 'admin', 'project-manager'].includes(String(currentUser?.role || '').toLowerCase());
    const allOfficersReceived = useMemo(() => {
       if (!project?.fieldOfficers || project.fieldOfficers.length === 0) return false;
       return project.fieldOfficers.every(officer => {
          const offId = typeof officer === 'object' ? (officer._id || officer.id) : officer;
          return (project.materialReceipts || []).some(r => String(r.confirmedBy?._id || r.confirmedBy) === String(offId));
       });
    }, [project?.fieldOfficers, project?.materialReceipts]);

    const hasPendingMaterial = (requests || []).length > 0 && !allDelivered;
    const isMaterialRequestComplete = allOfficersReceived || allDelivered || (requests || []).some(r => ['Site Received', 'Delivered', 'Received', 'Completed'].includes(r.status)) || String(project?.status || '').toLowerCase() === 'completed';


   const handleExportPaymentTransactions = () => {
      const allTxs = project?.paymentSummary?.transactions || [];
      // Only export transactions that are fully paid or are credits (which are inherently received payments)
      const txs = allTxs.filter(t => t.kind === 'credit' || t.requestStatus === 'paid');
      
      const headers = [
         'Client Name', 'Project ID', 'Customer Project ID', 'Customer', 'Site Name', 'PO Status', 'Location', 'PO Value', 'Budget',
         'Transaction ID', 'Date', 'Type', 'Category', 'Role Type', 'EP Type',
         'Description/Reason', 'Requested By', 'Requested Amount', 'Approved Amount', 
         'Disbursed Amount', 'Status', 'Payment Method', 'Ref ID', 'Bank Name', 
         'Cheque Number', 'UPI/Phone', 'Receiver Name', 'Internal Notes'
      ];

      const escapeCsv = (val) => {
         if (val === null || val === undefined) return '""';
         if (typeof val === 'number') return val;
         const str = String(val).replace(/"/g, '""').replace(/\r?\n|\r/g, ' ');
         return `"${str}"`;
      };

      const rows = txs.map(t => [
         escapeCsv(project?.customerName || ''),
         escapeCsv(project?.projectId || ''),
         escapeCsv(project?.applicationId || ''),
         escapeCsv(project?.projectName || ''),
         escapeCsv(project?.siteName || ''),
         escapeCsv(project?.status || ''),
         escapeCsv([project?.address?.city, project?.address?.state].filter(Boolean).join(', ') || ''),
         escapeCsv(project?.projectValue || 0),
         escapeCsv(project?.targetCost || 0),
         escapeCsv(t.transactionId || t.txnId || (t._id ? `TXN-${String(t._id).slice(-6).toUpperCase()}` : '')),
         escapeCsv(t.transactionDate ? new Date(t.transactionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''),
         escapeCsv(t.kind === 'credit' ? 'CREDIT' : 'EXPENSE'),
         escapeCsv(t.categoryLabel || t.expenseCategory || t.paymentType || ''),
         escapeCsv(t.roleType || ''),
         escapeCsv(t.epType || ''),
         escapeCsv(t.expenseReason || t.internalNotes || ''),
         escapeCsv(t.requestedBy?.name || t.createdBy?.name || ''),
         escapeCsv(t.amount || 0),
         escapeCsv(t.approvedAmount || 0),
         escapeCsv(t.disbursedAmount || 0),
         escapeCsv(t.requestStatus || 'paid'),
         escapeCsv(t.paymentMethod || ''),
         escapeCsv(t.refId || ''),
         escapeCsv(t.bankName || ''),
         escapeCsv(t.chequeNumber || ''),
         escapeCsv(t.upiPhone || t.receiverPhone || ''),
         escapeCsv(t.receiverName || t.recipient || ''),
         escapeCsv(t.internalNotes || t.approvalNote || '')
      ].join(','));

      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${project?.projectId || 'Project'}_payment_transactions.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
   };

   const progressData = useMemo(() => {
      const latestUpdatesByTrade = (project?.activityLog || [])
         .filter(log => log.type === 'work_update')
         .map(update => {
            const match = update.message?.match(/Site update \((.*?)\):\s+(.*?)(?:\s+—\s*(.*))?$/);
            return {
               trade: match ? match[1].toLowerCase() : 'unknown',
               originalTrade: match ? match[1] : 'Unknown',
               status: match ? match[2] : 'Unknown',
               description: update.detail,
               actorName: update.actorName,
               actorRole: update.actorRole,
               progress: update.progress,
               siteName: project?.customerName,
               at: update.at,
               images: update.images || []
            };
         })
         .filter(update => update.trade !== 'unknown')
         .sort((a, b) => new Date(a.at) - new Date(b.at))
         .reduce((acc, curr) => {
            acc[curr.trade] = curr;
            return acc;
         }, {});

      const validStatuses = ['DISPATCHED', 'DELIVERED', 'RECEIVED', 'COMPLETED', 'SITE RECEIVED'];
      const activeAssignedOfficers = (project?.fieldOfficers || []).filter(fo => typeof fo === 'object' && Boolean(fo.installationTrade || fo.role));

      const uniqueAssignedTrades = Array.from(new Set(
         activeAssignedOfficers.map(fo => (fo.installationTrade || fo.role).toLowerCase())
      ));

      // Check if ANY material request has been dispatched/delivered
      const anyDispatched = (requests || []).some(r =>
         validStatuses.includes(String(r.status || '').toUpperCase())
      );

      // If any request is dispatched, show ALL assigned trades (including members added after dispatch).
      // Otherwise, only show trades that have their own dispatched request.
      const activeTrades = anyDispatched
         ? uniqueAssignedTrades
         : uniqueAssignedTrades.filter(tradeValue => {
            return (requests || []).some(r => {
               const reqTrade = r.installationTrade?.toLowerCase();
               return reqTrade === tradeValue && validStatuses.includes(String(r.status || '').toUpperCase());
            });
         });

      if (activeTrades.length === 0) return [];

      return activeTrades.map(tradeValue => {
         const assignedOfficer = activeAssignedOfficers.find(fo => (fo.installationTrade || fo.role)?.toLowerCase() === tradeValue);
         const assignedName = assignedOfficer?.name || '-';

         const log = latestUpdatesByTrade[tradeValue];

         if (log) {
            // A work update exists — use its progress value
            return {
               ...log,
               trade: tradeValue,
               assignedName: assignedName,
               requestId: tradeValue
            };
         }

         // No work update yet — always show 0% / Not Started
         return {
            trade: tradeValue,
            assignedName: assignedName,
            status: 'Not Started',
            description: '-',
            progress: 0,
            siteName: project?.siteName || project?.customerName || '-',
            at: null,
            requestId: tradeValue
         };
      });
   }, [project, requests]);

   return (
      <div className="animate-in slide-in-from-bottom duration-500 w-full flex flex-col gap-4 pb-12">
         {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

         {/* Accordion Container */}
         <div className="flex flex-col gap-3">
            
            {/* 1. Project Manager Assignment */}
            <AccordionSection
               title="Project Manager"
               icon={<UserCircle className="w-5 h-5" />}
               isOpen={openSections['pm']}
               onToggle={() => toggleSection('pm')}
               isCompleted={isPmAssigned}
            >
               <ProjectManagerAssignment 
                  project={project} 
                  onUpdate={onUpdate} 
                  getInitials={getInitials} 
                  onAssignClick={() => setActiveModal('pm')}
               />
            </AccordionSection>

            {/* 2. Designer */}
            {/* <AccordionSection
               title="Designer"
               icon={<UserCircle className="w-5 h-5" />}
               isOpen={openSections['designer']}
               onToggle={() => toggleSection('designer')}
               isCompleted={false}
            >
               <div className="p-6 text-sm text-slate-500 italic bg-white flex justify-center border-t border-slate-100">
                  Designer assignment module coming soon...
               </div>
            </AccordionSection> */}

            {/* 3. Team Assignment */}
            <AccordionSection
               title="Team Assignment"
               icon={<Users2 className="w-5 h-5" />}
               isOpen={openSections['team']}
               onToggle={() => toggleSection('team')}
               isCompleted={isTeamAssigned}
            >
               <OverviewActivityTables
                  project={project}
                  formatCurrency={formatCurrency}
                  onUpdate={onUpdate}
                  teamActionsLocked={tasksInventoryLocked}
                  hideTransactions={true}
                  hideTasks={true}
                  onAddMemberClick={() => setActiveModal('team')}
                  onRequestExpense={(row, spentAmt = 0) => {
                     const tCost = project?.paymentSummary?.targetCost ?? project?.targetCost ?? 0;
                     if (!tCost || tCost === 0) {
                        setToast({ message: "Unable to raise expense: Target Cost is not set (₹0).", type: "error" });
                        return;
                     }

                     setExpenseInitialData({
                        requestedBy: String(row._id || row.id || ''),
                        roleType: row.installationTrade || 'not_applicable',
                        epType: 'not_applicable',
                        relatedToProject: 'yes',
                        projectId: project._id,
                        category: 'project',
                        spentSoFar: spentAmt,
                        isFromTeamTable: true
                     });
                     setIsExpenseModalOpen(true);
                  }}
               />
            </AccordionSection>

            {/* 4. Material Request */}
            <AccordionSection
               title="Material Request"
               icon={<Package className="w-5 h-5" />}
               isOpen={openSections['inventory']}
               onToggle={() => toggleSection('inventory')}
               isCompleted={isMaterialRequestComplete}
               statusLabel={!isMaterialRequestComplete ? "Waiting for field staff to confirm" : null}
            >
               <ProjectInventoryTab 
                  project={project} 
                  tasksInventoryLocked={tasksInventoryLocked} 
                  viewerRole={(JSON.parse(localStorage.getItem('blum_user') || localStorage.getItem('venus_user') || localStorage.getItem('user') || '{}')).role}
                  hideHeader={false}
                  onUpdate={() => onUpdate(true)}
               />
            </AccordionSection>
             {/* 5. Project Progress */}
             <AccordionSection
                 title="Project Progress"
                 icon={<CheckSquare className="w-5 h-5" />}
                 isOpen={openSections['tasks']}
                 onToggle={() => toggleSection('tasks')}
                 isCompleted={project?.installationMilestones?.carpenter && project?.installationMilestones?.electrician && project?.installationMilestones?.painter && project?.installationMilestones?.plumber}
             >
               <div className="flex flex-col bg-white border border-slate-100 rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-slate-100 flex items-center justify-end">
                      <button
                         type="button"
                         onClick={() => {
                            const firstTrade = progressData.length > 0 ? progressData[0].trade : 'carpenter';
                            setUpdateTrade(firstTrade);
                         }}
                         className="flex items-center gap-2 px-4 py-2 border border-[#2b448f] bg-white text-[#2b448f] hover:bg-slate-50 rounded-md text-[11px] font-semibold tracking-wider transition-colors shadow-sm"
                      >
                         <Plus className="w-4 h-4" /> Work Update
                      </button>
                  </div>
                  <DataTable 
                     columns={[
                        {
                           header: 'Name',
                           key: 'assignedName',
                           render: (row) => (
                              <div className="flex items-center gap-2">
                                 <span className="text-[11px] font-bold text-slate-700">{row.assignedName}</span>
                              </div>
                           )
                        },
                        {
                           header: 'Role type',
                           key: 'trade',
                           render: (row) => (
                              <span className="px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-widest bg-slate-100 text-slate-600">
                                 {row.trade}
                              </span>
                           )
                        },
                        {
                           header: 'Site Name',
                           key: 'siteName',
                           render: (row) => (
                              <span className="text-[11px] font-bold text-slate-800 uppercase tracking-tight">{row.siteName || '-'}</span>
                           )
                        },
                        {
                           header: 'Work Update Status',
                           key: 'status',
                           render: (row) => {
                              const sStatus = row.status || '-';
                              let colorClass = 'bg-slate-50 text-slate-600 border-slate-200';
                              if (String(sStatus).toLowerCase() === 'in progress' || String(sStatus).toLowerCase() === 'started') colorClass = 'bg-blue-50 text-blue-700 border-blue-200';
                              if (String(sStatus).toLowerCase() === 'on hold' || String(sStatus).toLowerCase() === 'waiting for material') colorClass = 'bg-rose-50 text-rose-700 border-rose-200';
                              if (String(sStatus).toLowerCase() === 'completed') colorClass = 'bg-emerald-50 text-emerald-700 border-emerald-200';
                              
                              return (
                                 <span className={`px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase tracking-tight border ${colorClass}`}>
                                    {sStatus}
                                 </span>
                              );
                           }
                        },
                        {
                           header: 'Notes',
                           key: 'notes',
                           render: (row) => (
                              <span className="text-[11px] font-semibold text-slate-500 line-clamp-1 max-w-[150px]" title={row.description}>
                                 {row.description || '-'}
                              </span>
                           )
                        },
                        {
                           header: 'Progress',
                           key: 'progress',
                           render: (row) => (
                              <div className="flex items-center gap-2">
                                 <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div 
                                       className={`h-full ${row.progress === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`} 
                                       style={{ width: `${row.progress || 0}%` }}
                                    />
                                 </div>
                                 <span className="text-[10px] font-bold text-slate-600">{row.progress || 0}%</span>
                              </div>
                           )
                        },
                        {
                           header: 'Action',
                           key: 'action',
                           render: (row) => (
                              <button 
                                 onClick={() => setHistoryTrade(row.trade)}
                                 className="p-1.5 rounded-md text-slate-400 hover:text-[#2b448f] hover:bg-slate-100 transition-colors"
                                 title="View History"
                              >
                                 <Eye className="w-4 h-4" />
                              </button>
                           )
                        }
                     ]}
                     data={progressData}
                     wrapperClassName=""
                  />
               </div>
               
               {/* Trade History Modal */}
               <TradeWorkUpdatesHistoryModal 
                  isOpen={!!historyTrade}
                  onClose={() => setHistoryTrade(null)}
                  trade={historyTrade}
                  updates={(project?.activityLog || [])
                     .filter(log => log.type === 'work_update')
                     .map(update => {
                        const match = update.message?.match(/Site update \((.*?)\):\s+(.*?)(?:\s+—\s*(.*))?$/);
                        return {
                           trade: match ? match[1] : 'Unknown',
                           status: match ? match[2] : 'Unknown',
                           description: update.detail,
                           actorName: update.actorName,
                           actorRole: update.actorRole,
                           progress: update.progress,
                           at: update.at,
                           images: update.images || []
                        };
                     })
                     .filter(u => u.trade === historyTrade)
                     .sort((a, b) => new Date(b.at) - new Date(a.at))
                  }
               />
            </AccordionSection>

            {/* 6. Documents Upload */}
            <AccordionSection
               title="Designs / Documents"
               icon={<FileText className="w-5 h-5" />}
               isOpen={openSections['documents']}
               onToggle={() => toggleSection('documents')}
               isCompleted={isDocumentsApproved}
               statusLabel={!isDocumentsApproved && hasDocumentsUploaded ? 'Pending Approval' : null}
            >
               <div className="flex flex-col bg-white border border-slate-100 rounded-xl overflow-hidden">
                   <div className="p-4 border-b border-slate-100 flex items-center justify-end gap-3">
                       {!project?.documentsStageComplete && 
                        ['owner', 'admin', 'project-manager'].includes(String(currentUser?.role || '').toLowerCase()) && (
                          <button
                             onClick={handleApproveStage}
                             disabled={isApproving || !hasDocumentsUploaded || !isInDocumentStage}
                             title={
                                !isInDocumentStage 
                                   ? 'Approval is only available during the Documents phase' 
                                   : !hasDocumentsUploaded 
                                      ? 'Upload at least one document before approving' 
                                      : 'Approve all uploaded documents to complete this stage'
                             }
                             className={`flex items-center gap-2 px-4 py-2 text-white rounded-md text-[11px] font-semibold shadow-sm transition-colors ${
                               isApproving || !hasDocumentsUploaded || !isInDocumentStage
                                 ? 'bg-slate-300 cursor-not-allowed opacity-60'
                                 : 'bg-blue-500 hover:bg-blue-600 cursor-pointer'
                             }`}
                          >
                             {isApproving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                             {isApproving ? 'Approving...' : 'Approve Documents Stage'}
                          </button>
                       )}

                      <button
                         onClick={() => canUploadDocs && setActiveModal('upload')}
                         disabled={!canUploadDocs}
                         title={!canUploadDocs ? "Only specific roles can upload documents" : ""}
                         className={`flex items-center cursor-pointer gap-2 px-4 py-2 rounded-md text-[11px] font-semibold shadow-sm transition-colors ${
                            !canUploadDocs ? 'bg-slate-300 text-white cursor-not-allowed border-none' : 'border border-[#2b448f] bg-white text-[#2b448f] hover:bg-slate-50'
                         }`}
                      >
                         <Plus className="w-3.5 h-3.5" />
                         Upload 
                      </button>
                   </div>
                   <DataTable 
                      columns={DOCUMENT_COLUMNS(
                         async (doc) => {
                            try {
                               const response = await fetch(`${apiFileBase()}${doc.path}`);
                               const blob = await response.blob();
                               const blobUrl = window.URL.createObjectURL(blob);
                               const link = document.createElement('a');
                               link.href = blobUrl;
                               link.download = doc.originalName || 'document';
                               document.body.appendChild(link);
                               link.click();
                               document.body.removeChild(link);
                               window.URL.revokeObjectURL(blobUrl);
                            } catch (err) {
                               window.open(`${apiFileBase()}${doc.path}`, '_blank');
                            }
                         },
                         // onView handler
                         (doc) => {
                            window.open(`${apiFileBase()}${doc.path}`, '_blank');
                         },
                         // onDelete handler
                         async (doc) => {
                            if (window.confirm("Are you sure you want to delete this document?")) {
                               try {
                                  await projectService.deleteDocument(project._id, doc._id);
                                  Toast.show('success', 'Document deleted successfully');
                                  if (typeof window.refreshProject === 'function') {
                                     window.refreshProject();
                                  } else {
                                     window.location.reload();
                                  }
                               } catch (error) {
                                  Toast.show('error', 'Failed to delete document');
                               }
                            }
                         },
                         // onApprove handler
                         (doc) => {
                            setDocumentToApprove(doc);
                         }
                      )}
                      data={project?.mergedDocuments || []}
                     noScroll={true}
                     wrapperClassName=""
                  />
               </div>
            </AccordionSection>

            {/* 7. Payment Transactions */}
            <AccordionSection
               title="Payment Transactions"
               icon={<CreditCard className="w-5 h-5" />}
               isOpen={openSections['advance']}
               onToggle={() => { toggleSection('advance'); setTxTab('credits'); }}
               isCompleted={isAdvancePaid}
            >
               <div className="flex flex-col">
                  {/* Top bar: tabs + action buttons */}
                  <div className="flex items-center justify-between px-4 pt-3 pb-0 bg-white border-b border-slate-100">
                     {/* Tabs */}
                     <div className="flex gap-0">
                        <button
                           onClick={() => setTxTab('credits')}
                           className={`flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest border-b-2 transition-all ${
                              txTab === 'credits'
                                ? 'border-emerald-500 text-emerald-600'
                                : 'border-transparent text-slate-400 hover:text-slate-600'
                           }`}
                        >
                           <CreditCard className="w-3.5 h-3.5" />
                           Credits
                           <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-black ${
                              txTab === 'credits' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                           }`}>
                              {transactionRows.filter(r => r.typeLabel === 'Credit' || r.typeLabel === 'Advance').length}
                           </span>
                        </button>
                        <button
                           onClick={() => setTxTab('expenses')}
                           className={`flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest border-b-2 transition-all ${
                              txTab === 'expenses'
                                ? 'border-rose-500 text-rose-600'
                                : 'border-transparent text-slate-400 hover:text-slate-600'
                           }`}
                        >
                           <Receipt className="w-3.5 h-3.5" />
                           Expenses
                           <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-black ${
                              txTab === 'expenses' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-500'
                           }`}>
                              {transactionRows.filter(r => r.typeLabel === 'Expense' || r.typeLabel === 'Expense (pending)').length}
                           </span>
                        </button>
                     </div>
                     {/* Action buttons */}
                     <div className="flex gap-2 pb-2">
                        <button
                           onClick={handleExportPaymentTransactions}
                           className="flex items-center cursor-pointer gap-2 px-3 py-1.5 border border-emerald-600 bg-transparent text-emerald-600 hover:bg-emerald-50 rounded-md text-[11px] font-semibold shadow-sm"
                        >
                           <Download className="w-3.5 h-3.5" />
                           Export All
                        </button>
                        <button
                           onClick={() => setActiveModal('payment')}
                           className="flex items-center cursor-pointer gap-2 px-3 py-1.5 border border-[#2b448f] bg-white text-[#2b448f] hover:bg-slate-50 rounded-md text-[11px] font-semibold shadow-sm"
                        >
                           <Plus className="w-3.5 h-3.5" />
                           Record Payment
                        </button>
                     </div>
                  </div>

                  {/* Credits tab */}
                  {txTab === 'credits' && (() => {
                     const rows = transactionRows.filter(r => r.typeLabel === 'Credit' || r.typeLabel === 'Advance');
                     const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
                     return (
                        <div className="animate-in fade-in duration-200">
                           <DataTable
                              columns={TRANSACTION_COLUMNS(formatCurrency, formatRowDateTime)}
                              data={rows}
                              emptyMessage="No credit transactions recorded yet."
                           />
                           {rows.length > 0 && (
                              <div className="flex justify-end items-center gap-2 px-4 py-2.5 bg-emerald-50 border-t border-emerald-100">
                                 <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-widest">Total Received</span>
                                 <span className="text-[14px] font-black text-emerald-700">₹{formatCurrency(total)}</span>
                              </div>
                           )}
                        </div>
                     );
                  })()}

                  {/* Expenses tab */}
                  {txTab === 'expenses' && (() => {
                     const rows = transactionRows.filter(r => r.typeLabel === 'Expense' || r.typeLabel === 'Expense (pending)');
                     const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
                     return (
                        <div className="animate-in fade-in duration-200">
                           <DataTable
                              columns={EXPENSE_COLUMNS(formatCurrency, formatRowDateTime)}
                              data={rows}
                              emptyMessage="No expenses recorded yet."
                           />
                           {rows.length > 0 && (
                              <div className="flex justify-end items-center gap-2 px-4 py-2.5 bg-rose-50 border-t border-rose-100">
                                 <span className="text-[11px] font-bold text-rose-700 uppercase tracking-widest">Total Expenses</span>
                                 <span className="text-[14px] font-black text-rose-700">₹{formatCurrency(total)}</span>
                              </div>
                           )}
                        </div>
                     );
                  })()}
               </div>
            </AccordionSection>
         </div>

         {/* Modals for Forms */}
         <Modal 
            isOpen={activeModal === 'pm'} 
            onClose={() => setActiveModal(null)}
            title="Assign Project Manager"
         >
            <ProjectManagerAssignmentForm 
               project={project}
               onUpdate={onUpdate}
               onClose={() => setActiveModal(null)}
            />
         </Modal>

         <Modal 
            isOpen={activeModal === 'payment'} 
            onClose={() => setActiveModal(null)}
            title="Record Payment"
            width="max-w-2xl"
         >
            <PaymentManagement 
               project={project}
               onUpdate={(updated) => {
                  onUpdate(updated);
                  setActiveModal(null);
               }}
               onClose={() => setActiveModal(null)}
               setToast={setToast}
            />
         </Modal>

         <Modal 
            isOpen={activeModal === 'team'} 
            onClose={() => setActiveModal(null)}
            title="Assign Team Members"
            width="max-w-xl"
         >
            <TeamAssignmentForm 
               project={project}
               onUpdate={onUpdate}
               onClose={() => setActiveModal(null)}
            />
         </Modal>

         <ScheduleTaskModal
            isOpen={activeModal === 'task' || activeModal === 'editTask'}
            onClose={() => {
               setActiveModal(null);
               setSelectedTask(null);
            }}
            project={project}
            eligibleMembers={eligibleFieldOfficers}
            taskToEdit={activeModal === 'editTask' ? selectedTask : null}
            onSuccess={() => {
               setActiveModal(null);
               setSelectedTask(null);
               if (onUpdate) onUpdate(true);
               if (refreshTasks) refreshTasks();
            }}
         />

         <Modal 
            isOpen={activeModal === 'upload'} 
            onClose={() => setActiveModal(null)}
            title="Upload Project Documents"
            footer={
               docModalState.canUpload && (
                  <div className="flex justify-end w-full">
                     <button
                        onClick={() => docFormRef.current?.submit()}
                        disabled={docModalState.submitting || !docModalState.hasFiles}
                        className="h-10 px-6 rounded-lg bg-[#f97316] text-white text-[12px] font-bold inline-flex items-center gap-2 transition-all duration-300 ease-in-out disabled:opacity-50 hover:bg-[#ea580c]"
                     >
                        {docModalState.submitting ? (
                           <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                           <Upload className="w-4 h-4" />
                        )}
                        Upload documents
                     </button>
                  </div>
               )
            }
         >
            <ProjectDocumentsTab 
               ref={docFormRef}
               project={project}
               onProjectUpdated={(updated) => {
                  setActiveModal(null);
                  if (onUpdate) onUpdate(updated);
               }}
               onStateChange={setDocModalState}
            />
         </Modal>

         <AddWorkUpdateModal
            isOpen={Boolean(workUpdateTask)}
            onClose={() => setWorkUpdateTask(null)}
            task={workUpdateTask}
            onSuccess={async () => {
               setWorkUpdateTask(null);
               if (onUpdate) onUpdate(true);
               if (refreshTasks) refreshTasks();
            }}
         />

         <TradeWorkUpdateModal
            isOpen={Boolean(updateTrade)}
            onClose={() => setUpdateTrade(null)}
            project={project}
            trade={updateTrade}
            setToast={setToast}
            onSuccess={() => {
               setUpdateTrade(null);
               if (onUpdate) onUpdate();
            }}
         />

         {activeModal === 'taskDetail' && (
            <TaskDetailsModal
               isOpen={true}
               onClose={() => setActiveModal(null)}
               task={selectedTask}
            />
         )}

         <Modal 
            isOpen={!!documentToApprove} 
            onClose={() => setDocumentToApprove(null)}
            title="Approve Document"
         >
            <div className="p-4 space-y-4">
               <p className="text-sm text-slate-600">
                  Are you sure you want to approve the document: <strong>{documentToApprove?.originalName}</strong>?
               </p>
               <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                  <button
                     type="button"
                     onClick={() => setDocumentToApprove(null)}
                     className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
                  >
                     Cancel
                  </button>
                  <button
                     type="button"
                     onClick={async () => {
                        try {
                           const { data } = await api.post(`/verve-projects/${project._id}/documents/${documentToApprove._id}/approve`);
                           setToast({ message: 'Document approved successfully', type: 'success' });
                           if (data) onUpdate(data);
                           setDocumentToApprove(null);
                        } catch (error) {
                           setToast({ message: 'Failed to approve document', type: 'error' });
                        }
                     }}
                     className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors shadow-sm"
                  >
                     Approve
                  </button>
               </div>
            </div>
         </Modal>


      </div>
   );
};
